// Pool 模式管理后台 API 客户端
// 对接 jingque-match 服务 /api/jingque/admin/pool/* 路由
// 文档: services/jingque-match/internal/modules/poolmodules/README.md

const POOL_BASE = 'https://api.dianziyang.com/api/jingque/admin/pool'

function getAuthHeader(): Record<string, string> {
  const token = localStorage.getItem('auth_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${POOL_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
      ...(init?.headers || {}),
    },
  })
  if (!res.ok) {
    if (res.status === 401) {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('auth_user')
      window.location.href = '/'
    }
    const text = await res.text()
    let err: any
    try { err = JSON.parse(text) } catch { err = { error: text } }
    const e: any = new Error(err.error || `请求失败: ${res.status}`)
    e.status = res.status
    throw e
  }
  if (res.status === 204) return null as T
  return res.json()
}

// ============ Types ============

export interface Plan {
  id: number
  name: string
  max_encounters: number
  daily_push_cap: number
  cooling_days: number
  cooling_breaks_allowed: number
  price_cents?: number
  duration_days: number
  is_trial: boolean
  status: 'active' | 'archived'
  created_at: string
  updated_at: string
}

export interface Member {
  user_id: string
  nickname?: string
  status: 'active' | 'cooling' | 'paused' | 'silenced' | 'blocked'
  plan_id?: number
  plan_expires_at?: string
  encounters_used: number
  cooling_breaks_used: number
  misconduct_forgiven_count: number
  no_show_count: number
  cooling_started_at?: string
  cooling_ends_at?: string
  blocked_at?: string
  enrolled_at: string
}

export interface Transaction {
  id: number
  user_id: string
  plan_id: number
  transaction_type: 'purchase' | 'refund' | 'upgrade' | 'extend' | 'manual_grant' | 'expiry'
  amount_cents?: number
  started_at: string
  expires_at?: string
  operator_id?: string
  note?: string
  created_at: string
}

export const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  purchase: '购买',
  refund: '退款',
  upgrade: '升级',
  extend: '续期',
  manual_grant: '管理员分配',
  expiry: '到期',
}

export const TRANSACTION_TYPE_TAG_COLOR: Record<string, string> = {
  purchase: 'blue',
  refund: 'orange',
  upgrade: 'purple',
  extend: 'cyan',
  manual_grant: 'green',
  expiry: 'default',
}

export const formatTransactionAmount = (cents?: number) =>
  cents != null ? `¥${(cents / 100).toFixed(2)}` : '-'

export interface PoolEncounter {
  id: number
  activity_id: number
  user_a_id: string
  user_b_id: string
  mode: 'pool' | 'event'
  status: 'pending' | 'confirmed' | 'completed' | 'no_show' | 'cancelled'
  scheduled_at?: string
  confirmed_at?: string
  completed_at?: string
  no_show_user_id?: string
  location?: string
  notes?: string
  created_at: string
  updated_at: string
}

export interface ProfileIteration {
  id: number
  user_id: string
  source: 'conversation' | 'feedback_positive' | 'feedback_negative' | 'admin_manual' | 'restock'
  old_version?: number
  new_version: number
  old_tags?: string[]
  new_tags?: string[]
  trigger_event?: string
  trigger_ref_id?: string
  created_at: string
}

export interface MemberDetail {
  member: Member
  iterations: ProfileIteration[]
  nickname?: string
}

// ============ Plans ============

export const listPlans = () => request<{ plans: Plan[] }>('/plans')

export const getPlan = (id: number) => request<Plan>(`/plans/${id}`)

export const createPlan = (data: Partial<Plan>) =>
  request<Plan>('/plans', { method: 'POST', body: JSON.stringify(data) })

export const updatePlan = (id: number, data: Partial<Plan>) =>
  request<Plan>(`/plans/${id}`, { method: 'PUT', body: JSON.stringify(data) })

export const archivePlan = (id: number) =>
  request<void>(`/plans/${id}`, { method: 'DELETE' })

// ============ Members ============

export const listMembers = (params?: { status?: string; limit?: number }) => {
  const qs = new URLSearchParams()
  if (params?.status) qs.set('status', params.status)
  if (params?.limit) qs.set('limit', String(params.limit))
  const suffix = qs.toString() ? `?${qs}` : ''
  return request<{ members: Member[] }>(`/members${suffix}`)
}

export const getMember = (userId: string) =>
  request<MemberDetail>(`/members/${encodeURIComponent(userId)}`)

export const pauseMember = (userId: string, note?: string) =>
  request<{ ok: boolean }>(`/members/${encodeURIComponent(userId)}/pause`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  })

export const resumeMember = (userId: string) =>
  request<{ ok: boolean }>(`/members/${encodeURIComponent(userId)}/resume`, {
    method: 'POST',
  })

export const grantPlan = (userId: string, planId: number, note?: string) =>
  request<{ member: Member; transaction: Transaction }>(
    `/members/${encodeURIComponent(userId)}/grant-plan`,
    { method: 'POST', body: JSON.stringify({ plan_id: planId, note }) }
  )

export const extendPlan = (userId: string, days: number, note?: string) =>
  request<{ member: Member; transaction: Transaction }>(
    `/members/${encodeURIComponent(userId)}/extend-plan`,
    { method: 'POST', body: JSON.stringify({ days, note }) }
  )

export const forgiveMisconduct = (userId: string) =>
  request<{ ok: boolean }>(
    `/members/${encodeURIComponent(userId)}/forgive-misconduct`,
    { method: 'POST' }
  )

export const hideProfile = (userId: string, activityId: number, reason: string, expiresAt?: string) =>
  request<{ ok: boolean }>(
    `/members/${encodeURIComponent(userId)}/hide`,
    { method: 'POST', body: JSON.stringify({ activity_id: activityId, reason, expires_at: expiresAt }) }
  )

export const releaseCooling = (userId: string) =>
  request<{ ok: boolean }>(
    `/members/${encodeURIComponent(userId)}/release-cooling`,
    { method: 'POST' }
  )

export const blockMember = (userId: string, reason: string) =>
  request<{ ok: boolean }>(
    `/members/${encodeURIComponent(userId)}/block`,
    { method: 'POST', body: JSON.stringify({ reason }) }
  )

export const unblockMember = (userId: string) =>
  request<{ ok: boolean }>(
    `/members/${encodeURIComponent(userId)}/unblock`,
    { method: 'POST' }
  )

// ============ Encounters ============

export const listEncounters = (params?: {
  status?: string
  activity_id?: string
  user_id?: string
  limit?: number
}) => {
  const qs = new URLSearchParams()
  if (params?.status) qs.set('status', params.status)
  if (params?.activity_id) qs.set('activity_id', params.activity_id)
  if (params?.user_id) qs.set('user_id', params.user_id)
  if (params?.limit) qs.set('limit', String(params.limit))
  const suffix = qs.toString() ? `?${qs}` : ''
  return request<{ encounters: AdminEncounter[] }>(`/encounters${suffix}`)
}

export const cancelEncounter = (id: number, reason: string) =>
  request<PoolEncounter>(`/encounters/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })

// ============ Transactions ============

export const listTransactions = (params?: { user_id?: string; plan_id?: number }) => {
  const qs = new URLSearchParams()
  if (params?.user_id) qs.set('user_id', params.user_id)
  if (params?.plan_id !== undefined) qs.set('plan_id', String(params.plan_id))
  const suffix = qs.toString() ? `?${qs}` : ''
  return request<{ transactions: Transaction[] }>(`/transactions${suffix}`)
}

// ============ Profiles ============

export interface PoolProfileListItem {
  user_id: string
  nickname?: string
  gender?: string
  age?: number
  height?: number
  education?: string
  city?: string
  occupation?: string
  have_tags: string[]
  want_tags: string[]
  status: string
  version: number
  updated_at?: string
  wechat?: string
  mbti?: string
}

export interface PoolProfile extends PoolProfileListItem {
  height?: number
  education?: string
  school_type?: string
  pref_min_age?: number
  pref_max_age?: number
  pref_min_height?: number
  pref_max_height?: number
  pref_education?: string[]
  pref_cities?: string[]
  have_info?: string
  want_info?: string
  not_have_info?: string
  not_want_info?: string
  personality_summary?: string
  lifestyle_summary?: string
  relationship_needs?: string
  mbti?: string
  wechat?: string
}

export interface PoolProfileDetail {
  profile: PoolProfile
  iterations: ProfileIteration[]
}

// PoolProfile (snake_case, pool 端点 /members/:userId/profile) →
// ProfileDTO (camelCase, ProfileDetailModal 所需)。
// 解决 pool 端点序列化键与共享 ProfileDetailModal 期望键不一致的问题
// (doc 77 bug fix: 之前用 as any 强转导致 Modal 显示「无标签」等)
export function poolProfileToDTO(p: PoolProfile): import('@/components/admin/ProfileDetailModal').ProfileDTO {
  return {
    userId: p.user_id,
    nickname: p.nickname,
    gender: p.gender,
    age: p.age,
    height: p.height,
    education: p.education,
    schoolType: p.school_type,
    city: p.city,
    occupation: p.occupation,
    haveTags: p.have_tags,
    wantTags: p.want_tags,
    haveInfo: p.have_info,
    wantInfo: p.want_info,
    notHaveInfo: p.not_have_info,
    notWantInfo: p.not_want_info,
    personalitySummary: p.personality_summary,
    lifestyleSummary: p.lifestyle_summary,
    relationshipNeeds: p.relationship_needs,
    mbti: p.mbti,
    wechat: p.wechat,
    status: p.status,
    version: p.version,
    updatedAt: p.updated_at,
    prefMinAge: p.pref_min_age,
    prefMaxAge: p.pref_max_age,
    prefMinHeight: p.pref_min_height,
    prefMaxHeight: p.pref_max_height,
    prefEducation: p.pref_education,
    prefCities: p.pref_cities,
  }
}

export const listProfiles = (params?: {
  activity_id?: string
  status?: string
  user_id?: string
  limit?: number
  offset?: number
}) => {
  const qs = new URLSearchParams()
  if (params?.activity_id) qs.set('activity_id', params.activity_id)
  if (params?.status) qs.set('status', params.status)
  if (params?.user_id) qs.set('user_id', params.user_id)
  if (params?.limit) qs.set('limit', String(params.limit))
  if (params?.offset) qs.set('offset', String(params.offset))
  const suffix = qs.toString() ? `?${qs}` : ''
  return request<{ profiles: PoolProfileListItem[]; total: number; count: number }>(`/profiles${suffix}`)
}

// ============ Profile Search (doc 77: 统一画像筛选 + 可选 rerank) ============

export interface ProfileSearchParams {
  genders?: string[]
  age_min?: number
  age_max?: number
  height_min?: number
  height_max?: number
  education?: string[]
  city?: string[]
  occupation?: string[]
  school_type?: string[]
  mbti?: string[]
  status?: string[]
  have_tags?: string[]
  have_tags_mode?: 'and' | 'or'
  want_tags?: string[]
  want_tags_mode?: 'and' | 'or'
  limit?: number
  offset?: number
  rerank?: boolean
  rerank_query?: string
  rerank_threshold?: number
}

export interface ProfileSearchItem {
  user_id: string
  nickname?: string
  gender?: string
  age?: number
  height?: number
  education?: string
  city?: string
  occupation?: string
  school_type?: string
  have_tags: string[]
  want_tags: string[]
  have_info?: string
  want_info?: string
  personality_summary?: string
  lifestyle_summary?: string
  relationship_needs?: string
  status: string
  version: number
  updated_at?: string
  wechat?: string
  mbti?: string
  rerank_score?: number
}

export const searchProfiles = (params: ProfileSearchParams) => {
  const qs = new URLSearchParams()
  const pushArr = (k: string, v?: string[]) => {
    if (v && v.length) qs.set(k, v.join(','))
  }
  const pushNum = (k: string, v?: number) => {
    if (v !== undefined && v !== null) qs.set(k, String(v))
  }
  pushArr('genders', params.genders)
  pushNum('age_min', params.age_min)
  pushNum('age_max', params.age_max)
  pushNum('height_min', params.height_min)
  pushNum('height_max', params.height_max)
  pushArr('education', params.education)
  pushArr('city', params.city)
  pushArr('occupation', params.occupation)
  pushArr('school_type', params.school_type)
  pushArr('mbti', params.mbti)
  pushArr('status', params.status)
  pushArr('have_tags', params.have_tags)
  if (params.have_tags_mode) qs.set('have_tags_mode', params.have_tags_mode)
  pushArr('want_tags', params.want_tags)
  if (params.want_tags_mode) qs.set('want_tags_mode', params.want_tags_mode)
  pushNum('limit', params.limit)
  pushNum('offset', params.offset)
  if (params.rerank) qs.set('rerank', 'true')
  if (params.rerank_query) qs.set('rerank_query', params.rerank_query)
  if (params.rerank_threshold !== undefined && params.rerank_threshold !== null) {
    qs.set('rerank_threshold', String(params.rerank_threshold))
  }
  const suffix = qs.toString() ? `?${qs}` : ''
  return request<{ profiles: ProfileSearchItem[]; total: number }>(`/profiles/search${suffix}`)
}

export const getProfile = (userId: string) =>
  request<PoolProfileDetail>(`/members/${encodeURIComponent(userId)}/profile`)

export interface UpdateProfileRequest {
  have_tags?: string[]
  want_tags?: string[]
  have_info?: string
  want_info?: string
  not_have_info?: string
  not_want_info?: string
  personality_summary?: string
  lifestyle_summary?: string
  relationship_needs?: string
  status?: string
}

export const updateProfile = (userId: string, data: UpdateProfileRequest) =>
  request<{ ok: boolean }>(`/members/${encodeURIComponent(userId)}/profile`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })

// ============ User Search ============

export interface PoolUserSearchItem {
  user_id: string
  nickname: string
  gender?: string
  age?: number
  city?: string
  occupation?: string
  profile_status?: string
  member_status?: string
  updated_at?: string
}

export const searchPoolUsers = (q: string, limit = 20) => {
  const qs = new URLSearchParams()
  if (q) qs.set('q', q)
  qs.set('limit', String(limit))
  return request<{ users: PoolUserSearchItem[]; count: number }>(`/users/search?${qs.toString()}`)
}

// ============ Recommendations ============

export type RecommendationStatus = 'pending' | 'pushed' | 'responded' | 'expired' | 'cancelled'
export type ResponseAction = 'accept' | 'skip' | 'lock'

export interface PoolRecommendation {
  id: number
  activity_id: string
  seeker_user_id: string
  target_user_id: string
  seeker_nickname?: string
  target_nickname?: string
  base_score: number
  weight_score: number
  final_score: number
  rank: number
  exclude_reasons?: string[]
  reason?: string
  status: RecommendationStatus
  response?: ResponseAction
  skip_reason_code?: string
  skip_reason_note?: string
  pushed_at?: string
  responded_at?: string
  expires_at: string
  viewed: boolean
  viewed_at?: string
  created_at: string
  updated_at: string
}

export const listRecommendations = (params?: {
  activity_id?: string
  seeker_user_id?: string
  target_user_id?: string
  status?: RecommendationStatus
  response?: ResponseAction
  limit?: number
  offset?: number
  start_date?: string
  end_date?: string
}) => {
  const qs = new URLSearchParams()
  if (params?.activity_id) qs.set('activity_id', params.activity_id)
  if (params?.seeker_user_id) qs.set('seeker_user_id', params.seeker_user_id)
  if (params?.target_user_id) qs.set('target_user_id', params.target_user_id)
  if (params?.status) qs.set('status', params.status)
  if (params?.response) qs.set('response', params.response)
  if (params?.limit) qs.set('limit', String(params.limit))
  if (params?.offset !== undefined) qs.set('offset', String(params.offset))
  if (params?.start_date) qs.set('start_date', params.start_date)
  if (params?.end_date) qs.set('end_date', params.end_date)
  const suffix = qs.toString() ? `?${qs}` : ''
  return request<{ recommendations: PoolRecommendation[]; total: number }>(`/recommendations${suffix}`)
}

// ============ User Rec Stats (按用户聚合) ============

export interface UserRecStats {
  user_id: string
  nickname?: string
  pending_count: number
  pushed_count: number
  responded_count: number
  accept_count: number
  skip_count: number
  expired_count: number
  cancelled_count: number
  total_count: number
  last_pushed_at?: string
  last_rec_at?: string
  match_run_count: number
  last_match_at?: string
  ever_degraded: boolean
  ever_error: boolean
}

export const listUserRecStats = (params?: {
  activity_id?: string
  q?: string
  limit?: number
  offset?: number
  start_date?: string
  end_date?: string
}) => {
  const qs = new URLSearchParams()
  if (params?.activity_id) qs.set('activity_id', params.activity_id)
  if (params?.q) qs.set('q', params.q)
  if (params?.limit) qs.set('limit', String(params.limit))
  if (params?.offset !== undefined) qs.set('offset', String(params.offset))
  if (params?.start_date) qs.set('start_date', params.start_date)
  if (params?.end_date) qs.set('end_date', params.end_date)
  const suffix = qs.toString() ? `?${qs}` : ''
  return request<{ stats: UserRecStats[]; total: number }>(`/recommendations/stats-by-user${suffix}`)
}

// ============ Match Logs ============

export interface PoolMatchLog {
  id: number
  activity_id: string
  user_id: string
  nickname?: string
  trigger_source: string
  candidates_count: number
  excluded_count: number
  recommendations_count: number
  is_degraded: boolean
  duration_ms: number
  error_message?: string
  metadata?: Record<string, any>
  run_id?: string
  created_at: string
}

export const listMatchLogs = (params?: {
  activity_id?: string
  user_id?: string
  trigger_source?: string
  has_error?: boolean
  degraded?: boolean
  limit?: number
  offset?: number
  start_date?: string
  end_date?: string
}) => {
  const qs = new URLSearchParams()
  if (params?.activity_id) qs.set('activity_id', params.activity_id)
  if (params?.user_id) qs.set('user_id', params.user_id)
  if (params?.trigger_source) qs.set('trigger_source', params.trigger_source)
  if (params?.has_error !== undefined) qs.set('has_error', String(params.has_error))
  if (params?.degraded !== undefined) qs.set('degraded', String(params.degraded))
  if (params?.limit) qs.set('limit', String(params.limit))
  if (params?.offset !== undefined) qs.set('offset', String(params.offset))
  if (params?.start_date) qs.set('start_date', params.start_date)
  if (params?.end_date) qs.set('end_date', params.end_date)
  const suffix = qs.toString() ? `?${qs}` : ''
  return request<{ logs: PoolMatchLog[]; total: number }>(`/match-logs${suffix}`)
}

// ============ Push Logs (doc 90) ============

export interface PoolPushLog {
  id: number
  user_id: string
  nickname?: string
  target_user_id?: string
  target_nickname?: string
  recommendation_id?: number
  log_type: 'push' | 'skip' | 'error'
  skip_reason?: string
  error_message?: string
  channel: string
  sent_at?: string
  metadata?: Record<string, any>
  created_at: string
}

export const listPushLogs = (params?: {
  user_id?: string
  target_user_id?: string
  log_type?: string
  skip_reason?: string
  limit?: number
  offset?: number
  start_date?: string
  end_date?: string
}) => {
  const qs = new URLSearchParams()
  if (params?.user_id) qs.set('user_id', params.user_id)
  if (params?.target_user_id) qs.set('target_user_id', params.target_user_id)
  if (params?.log_type) qs.set('log_type', params.log_type)
  if (params?.skip_reason) qs.set('skip_reason', params.skip_reason)
  if (params?.limit) qs.set('limit', String(params.limit))
  if (params?.offset !== undefined) qs.set('offset', String(params.offset))
  if (params?.start_date) qs.set('start_date', params.start_date)
  if (params?.end_date) qs.set('end_date', params.end_date)
  const suffix = qs.toString() ? `?${qs}` : ''
  return request<{ logs: PoolPushLog[]; total: number }>(`/push-logs${suffix}`)
}

// ============ Pool Cron Status ============

export interface PoolCronStatus {
  name: string
  spec: string
  description: string
  last_run_at: string
  last_duration: string
  last_error?: string
  last_result?: string
  is_alive: boolean
  run_count: number
}

// Trace 视图共用 API(doc 69 Part C:在 PoolMembersPage 详情侧拉用)
export const getMemberTrace = async (userId: string) => {
  // 并行拉取 6 个数据源(doc 90: 增 push-logs)
  const [memberRes, profileRes, logsRes, recsRes, encountersRes, pushRes] = await Promise.all([
    request<MemberDetail>(`/members/${encodeURIComponent(userId)}`).catch(() => null),
    request<{ profile: any }>(`/members/${encodeURIComponent(userId)}/profile`).catch(() => ({ profile: null })),
    request<{ logs: PoolMatchLog[] }>(`/match-logs?user_id=${encodeURIComponent(userId)}&limit=10`).catch(() => ({ logs: [] })),
    request<{ recommendations: any[] }>(`/recommendations?seeker_user_id=${encodeURIComponent(userId)}&limit=20`).catch(() => ({ recommendations: [] })),
    request<{ encounters: PoolEncounter[] }>(`/encounters?user_id=${encodeURIComponent(userId)}`).catch(() => ({ encounters: [] })),
    request<{ logs: PoolPushLog[] }>(`/push-logs?user_id=${encodeURIComponent(userId)}&limit=15`).catch(() => ({ logs: [] })),
  ])
  return {
    member: memberRes,
    profile: profileRes?.profile,
    logs: logsRes?.logs || [],
    recs: recsRes?.recommendations || [],
    encounters: encountersRes?.encounters || [],
    pushLogs: pushRes?.logs || [],
  }
}

export interface PoolMatchStage {
  id: string
  stage: string
  status: string
  detail?: string // JSON 字符串
  duration_ms: number
  error?: string
  mode: string
  run_id?: string
  created_at: string
}

export const listMatchStages = (
  activity_id: string,
  user_id: string,
  run_id?: string,
) => {
  const qs = new URLSearchParams()
  if (run_id) qs.set('run_id', run_id)
  else {
    qs.set('activity_id', activity_id)
    qs.set('user_id', user_id)
  }
  return request<{ stages: PoolMatchStage[]; legacy?: boolean }>(`/match-logs/stages?${qs}`)
}

// ============ Trigger Match (doc 74) ============

export interface TriggerSummary {
  candidates_count: number
  excluded_count: number
  recommendations_count: number
  is_degraded: boolean
  duration_ms: number
  trigger_source: string
}

export interface TriggerRecommendation {
  id: number
  target_user_id: string
  base_score: number
  weight_score: number
  final_score: number
  rank: number
  reason?: string
  exclude_reasons?: string[]
}

export interface TriggerMatchResult {
  run_id?: string
  error?: string
  duration_ms: number
  summary?: TriggerSummary
  stages: PoolMatchStage[]
  recommendations?: TriggerRecommendation[]
}

export const triggerMatch = (activity_id: string, user_id: string) =>
  request<TriggerMatchResult>('/match-logs/trigger', {
    method: 'POST',
    body: JSON.stringify({ activity_id, user_id }),
  })

// ============ Manual Push (doc 76 - Phase D 双写;doc 84 带分数) ============

export interface PairScores {
  base_score: number
  seeker_weight: number
  seeker_final: number
  target_weight: number
  target_final: number
}

export interface ManualPushRequest {
  activity_id: string
  seeker_user_id: string
  target_user_id: string
  reason_seeker: string
  reason_target: string
  base_score: number // 必填(匹配度)
  seeker_weight?: number
  seeker_final?: number
  target_weight?: number
  target_final?: number
}

export interface ManualPushRec {
  id: number
  activity_id: string
  seeker_user_id: string
  target_user_id: string
  base_score?: number
  weight_score?: number
  final_score?: number
  rank?: number
  reason?: string
  status: string
  pushed_at?: string
  expires_at: string
  created_at: string
}

export interface ManualPushResponse {
  recommendations: ManualPushRec[]
}

export const generateReason = (seeker_user_id: string, target_user_id: string) =>
  request<{ reason_seeker?: string; reason_target?: string; scores?: PairScores; error?: string }>(
    '/recommendations/generate-reason',
    { method: 'POST', body: JSON.stringify({ seeker_user_id, target_user_id }) },
  )

export const manualPush = (params: ManualPushRequest) =>
  request<ManualPushResponse>('/recommendations/manual-push', {
    method: 'POST',
    body: JSON.stringify(params),
  })

export interface PushWarning {
  type: string
  message: string
}

export const precheckPush = (
  activity_id: string,
  seeker_user_id: string,
  target_user_id: string,
) =>
  request<{ warnings: PushWarning[] }>('/recommendations/manual-push/precheck', {
    method: 'POST',
    body: JSON.stringify({ activity_id, seeker_user_id, target_user_id }),
  })

export const listPoolCronStatus = () =>
  request<{ crons: PoolCronStatus[] }>('/cron-status')

// ============ Venues ============

export interface PoolVenue {
  id: number
  name: string
  region: string
  address?: string
  venue_type?: string
  status: string
  sort_order: number
  created_at: string
  updated_at: string
}

export interface VenueRequest {
  name: string
  region: string
  address?: string
  venue_type?: string
  status?: string
  sort_order?: number
}

export const listVenues = () =>
  request<{ venues: PoolVenue[] }>('/venues')

export const createVenue = (data: VenueRequest) =>
  request<{ venue: PoolVenue }>('/venues', { method: 'POST', body: JSON.stringify(data) })

export const updateVenue = (id: number, data: VenueRequest) =>
  request<{ ok: boolean }>(`/venues/${id}`, { method: 'PUT', body: JSON.stringify(data) })

export const deleteVenue = (id: number) =>
  request<{ ok: boolean }>(`/venues/${id}`, { method: 'DELETE' })

// ============ Admin Proxy Operations on seed_ Users (doc 79) ============
// admin 代 seed_ 用户触发响应 / 排期 / 可用时间 / 确认场地。所有接口后端校验 user_id 必须 seed_ 前缀。

// --- Recommendation respond ---

export const adminRespond = (
  recId: number,
  data: {
    user_id: string
    response: 'accept' | 'skip'
    skip_reason_code?: string
    skip_reason_note?: string
  },
) =>
  request<{ ok: boolean }>(`/recommendations/${recId}/respond`, {
    method: 'POST',
    body: JSON.stringify(data),
  })

// --- Encounter schedule / availability / venue ---

export interface AdminTimeExclusion {
  date: string // YYYY-MM-DD
  start: string // HH:mm
  end: string // HH:mm
}

export const adminSchedule = (
  encId: number,
  data: {
    user_id: string
    scheduled_at: string
    location?: string
    notes?: string
  },
) =>
  request<PoolEncounter>(`/encounters/${encId}/schedule`, {
    method: 'POST',
    body: JSON.stringify(data),
  })

export const adminSubmitAvailability = (
  encId: number,
  data: {
    user_id: string
    // doc 83: per-day slot 选择 {"YYYY-MM-DD": ["morning","evening"]}
    date_slots: Record<string, string[]>
    exclusions?: AdminTimeExclusion[]
    location?: string
  },
) =>
  request<{ status: string; mine?: any; partner_submitted?: boolean; overlap?: any; regions?: string[] }>(
    `/encounters/${encId}/availability`,
    { method: 'POST', body: JSON.stringify(data) },
  )

export const adminGetAvailability = (encId: number, user_id: string) =>
  request<AdminAvailabilityView>(
    `/encounters/${encId}/availability?user_id=${encodeURIComponent(user_id)}`,
  )

export const adminConfirmVenue = (
  encId: number,
  data: { user_id: string; venue_id: number },
) =>
  request<{ encounter: PoolEncounter }>(`/encounters/${encId}/confirm-venue`, {
    method: 'POST',
    body: JSON.stringify(data),
  })

// ============ Pair View (doc 79) ============
// 以"一对 (A,B)" 为单元并排展示双方选择,LEFT JOIN encounter 关联成局情况。

export type PairOutcome = 'mutual_accept' | 'one_sided_accept' | 'both_skip' | 'no_reverse' | 'pending'

export interface PairRow {
  activity_id: string
  user_a_id: string
  user_b_id: string
  user_a_nick: string
  user_b_nick: string
  outcome: PairOutcome
  // forward (A→B) latest active rec
  fwd_rec_id: number
  fwd_status: string
  fwd_response: string
  fwd_score?: number
  fwd_pushed_at?: string
  fwd_responded_at?: string
  fwd_skip_code?: string
  // reverse (B→A) latest active rec (可空)
  rev_rec_id: number
  rev_status: string
  rev_response: string
  rev_score?: number
  rev_pushed_at?: string
  rev_responded_at?: string
  rev_skip_code?: string
  // encounter (可空)
  encounter_id: number
  encounter_status: string
  encounter_location: string
  encounter_scheduled_at?: string
  any_created_at?: string
}

export const listPairs = (params?: {
  activity_id?: string
  user_id?: string
  outcome?: PairOutcome
  limit?: number
  offset?: number
  start_date?: string
  end_date?: string
}) => {
  const qs = new URLSearchParams()
  if (params?.activity_id) qs.set('activity_id', params.activity_id)
  if (params?.user_id) qs.set('user_id', params.user_id)
  if (params?.outcome) qs.set('outcome', params.outcome)
  if (params?.limit) qs.set('limit', String(params.limit))
  if (params?.offset !== undefined) qs.set('offset', String(params.offset))
  if (params?.start_date) qs.set('start_date', params.start_date)
  if (params?.end_date) qs.set('end_date', params.end_date)
  const suffix = qs.toString() ? `?${qs}` : ''
  return request<{ pairs: PairRow[]; total: number }>(`/pairs${suffix}`)
}

// 判断 user_id 是否为 seed_ 前缀的测试用户(用于控制代操作按钮显隐)
export const isSeedUser = (uid: string) => !!uid && uid.startsWith('seed_')

// ============ Admin Encounter Enhancement (doc 80) ============
// 后端 ListEncounters 改用 ListForAdmin(LEFT JOIN match_profiles ×2),返回双方昵称
// 前端用 AdminEncounter 替换裸 PoolEncounter

export interface AdminEncounter extends PoolEncounter {
  user_a_nick?: string
  user_b_nick?: string
}

// doc 80 简化版:fallback slot 由 system_config 算出来,不存 proposal 实体
export interface FallbackSlot {
  slot_time: string
  label: string
  my_vote?: 'accept' | 'reject'
  partner_vote?: 'accept' | 'reject'
}

// AvailabilityView 扩展(已有,加 fallback_slots 字段)
export interface AdminAvailabilityView {
  status: 'none' | 'waiting' | 'need_venue' | 'no_overlap' | 'confirmed'
  mine?: any
  partner_submitted?: boolean
  overlap?: { date: string; slot: string; scheduled_at: string }
  regions?: string[]
  fallback_slots?: FallbackSlot[]
}

// admin 代 seed_ 用户对兜底时段投票
export const adminVoteSlot = (
  encId: number,
  data: { user_id: string; slot_time: string; vote: 'accept' | 'reject' },
) =>
  request<{ ok: boolean; encounter?: AdminEncounter; cancelled?: boolean }>(
    `/encounters/${encId}/slot-vote`,
    { method: 'POST', body: JSON.stringify(data) },
  )

// doc 86: admin 代 seed_ 用户批量多选兜底时段(accept-only,无 reject)
export const adminVoteSlotBatch = (
  encId: number,
  data: { user_id: string; slot_times: string[] },
) =>
  request<{ ok: boolean; encounter?: AdminEncounter }>(
    `/encounters/${encId}/slot-vote-batch`,
    { method: 'POST', body: JSON.stringify(data) },
  )

// doc 86: admin 视角双方完整可用时间(无 seed 限制,partner 详情完整)
export interface AdminAvailabilityBothView {
  status: 'none' | 'waiting' | 'need_venue' | 'no_overlap' | 'confirmed'
  user_a?: {
    available_dates?: string[]
    time_slots?: string[]
    date_slots?: Record<string, string[]>
    location?: string
  }
  user_b?: {
    available_dates?: string[]
    time_slots?: string[]
    date_slots?: Record<string, string[]>
    location?: string
  }
  overlap?: { date: string; slot: string; scheduled_at: string }
  fallback_slots?: FallbackSlot[]
  regions?: string[]
}

export const adminGetAvailabilityBoth = (encId: number) =>
  request<AdminAvailabilityBothView>(`/encounters/${encId}/availability-all`)

// ────────────────── doc 113-P2 ③:FS 文档管理 ──────────────────

export interface FsDocListItem {
  path: string
  in_db: boolean
  updated_by?: string
  updated_at?: string
}

export interface FsDocDetail {
  path: string
  content: string
  source: 'db' | 'default'
  updated_by?: string
  updated_at?: string
}

export const fsDocsList = () =>
  request<{ docs: FsDocListItem[] }>('/fs-docs')

export const fsDocsGet = (path: string) =>
  request<FsDocDetail>(`/fs-docs/doc?path=${encodeURIComponent(path)}`)

export const fsDocsUpsert = (path: string, content: string) =>
  request<{ path: string; updated_by: string; updated_at: string }>('/fs-docs/doc', {
    method: 'PUT',
    body: JSON.stringify({ path, content }),
  })

export const fsDocsDelete = (path: string) =>
  request<{ reverted: boolean; path: string }>(
    `/fs-docs/doc?path=${encodeURIComponent(path)}`,
    { method: 'DELETE' },
  )
